import React, { useState, useRef, useEffect } from 'react';
import { Camera, Mic, Square, Play, Pause, ChevronLeft, RotateCcw } from 'lucide-react';
import API_URL from '../config/API_URL';

const PersonalBookRecordingPage = ({ book, onComplete }) => {
  const [recordingType, setRecordingType] = useState('webcam_only');
  const [recordingState, setRecordingState] = useState('idle');
  const [selectedKids, setSelectedKids] = useState([]);
  const [availableKids, setAvailableKids] = useState([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploadedRecordingId, setUploadedRecordingId] = useState(null);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Fetch available kids
  useEffect(() => {
    const fetchKids = async () => {
      try {
        const response = await fetch(`${API_URL}/api/family/my-families`, {
          credentials: 'include'
        });
        const families = await response.json();
        
        const allKids = [];
        for (const family of families) {
          const detailRes = await fetch(`${API_URL}/api/family/${family.id}`, {
            credentials: 'include'
          });
          const detail = await detailRes.json();
          
          if (detail.kids) {
            detail.kids.forEach(kid => {
              allKids.push({
                ...kid,
                familyName: detail.family_name,
                familyId: detail.id
              });
            });
          }
        }
        
        setAvailableKids(allKids);
      } catch (error) {
        console.error('Error fetching kids:', error);
      }
    };

    fetchKids();
  }, []);

  // Initialize webcam
  useEffect(() => {
    const initializeWebcam = async () => {
      if (recordingType === 'webcam_only') {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: true, 
            audio: false
          });
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          
          streamRef.current = stream;
        } catch (err) {
          console.error('Error accessing webcam:', err);
        }
      }
    };

    initializeWebcam();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [recordingType]);

  const startRecording = async () => {
    if (selectedKids.length === 0) {
      alert('Please select at least one child to record for');
      return;
    }

    setShowConfirmModal(false);

    try {
      let stream;
      
      if (recordingType === 'audio_only') {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } else {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: true, 
          audio: true 
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = handleRecordingComplete;
      mediaRecorderRef.current = mediaRecorder;
      
      chunksRef.current = [];
      mediaRecorder.start(1000);
      setRecordingState('recording');
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error starting recording:', err);
      alert('Could not access camera/microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecordingState('stopped');
      clearInterval(timerRef.current);
    }
  };

  const handleRecordingComplete = async () => {
    const blob = new Blob(chunksRef.current, { 
      type: recordingType === 'audio_only' ? 'audio/webm' : 'video/webm' 
    });
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    await uploadRecording(blob);
  };

  const uploadRecording = async (blob) => {
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('recording', blob);
      formData.append('personal_book_id', book.id);
      formData.append('recording_type', recordingType);
      formData.append('duration_seconds', recordingTime);
      formData.append('kid_ids', JSON.stringify(selectedKids.map(k => k.id)));
      
      const response = await fetch(`${API_URL}/api/personal-books/upload-recording`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setUploadedRecordingId(data.recording_id);
        setShowSaveConfirmModal(true);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload recording');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveAndContinue = () => {
    onComplete();
  };

  const handleDeleteAndRerecord = async () => {
    if (!uploadedRecordingId) return;
    
    setShowSaveConfirmModal(false);
    setIsUploading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/personal-books/delete-recording/${uploadedRecordingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        alert('Recording deleted. Ready to record again.');
        setUploadedRecordingId(null);
        resetRecording();
      } else {
        alert('Failed to delete recording. Please try again.');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete recording');
    } finally {
      setIsUploading(false);
    }
  };

  const resetRecording = () => {
    setRecordingState('idle');
    setRecordingTime(0);
    chunksRef.current = [];
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    streamRef.current = null;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '1rem 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={onComplete} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <ChevronLeft size={24} />
            </button>
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Recording: {book.title}</h1>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>by {book.author}</p>
            </div>
          </div>
          {recordingState !== 'idle' && (
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              color: recordingState === 'recording' ? '#dc2626' : '#6b7280',
              fontFamily: 'monospace'
            }}>
              {formatTime(recordingTime)}
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
          {/* Recording Area */}
          <div>
            {/* Recording Type Selection */}
            <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1rem' }}>
              <h3 style={{ marginBottom: '1rem', fontWeight: '600' }}>Recording Type</h3>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <label style={{
                  flex: 1,
                  padding: '1rem',
                  border: `2px solid ${recordingType === 'webcam_only' ? '#10b981' : '#e5e7eb'}`,
                  borderRadius: '0.5rem',
                  cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed',
                  background: recordingType === 'webcam_only' ? '#f0fdf4' : 'white',
                  opacity: recordingState === 'idle' ? 1 : 0.5
                }}>
                  <input
                    type="radio"
                    name="recordingType"
                    value="webcam_only"
                    checked={recordingType === 'webcam_only'}
                    onChange={(e) => setRecordingType(e.target.value)}
                    disabled={recordingState !== 'idle'}
                  />
                  <Camera size={20} style={{ marginLeft: '0.5rem' }} />
                  <span style={{ marginLeft: '0.5rem' }}>Video Recording</span>
                </label>
                
                <label style={{
                  flex: 1,
                  padding: '1rem',
                  border: `2px solid ${recordingType === 'audio_only' ? '#10b981' : '#e5e7eb'}`,
                  borderRadius: '0.5rem',
                  cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed',
                  background: recordingType === 'audio_only' ? '#f0fdf4' : 'white',
                  opacity: recordingState === 'idle' ? 1 : 0.5
                }}>
                  <input
                    type="radio"
                    name="recordingType"
                    value="audio_only"
                    checked={recordingType === 'audio_only'}
                    onChange={(e) => setRecordingType(e.target.value)}
                    disabled={recordingState !== 'idle'}
                  />
                  <Mic size={20} style={{ marginLeft: '0.5rem' }} />
                  <span style={{ marginLeft: '0.5rem' }}>Audio Only</span>
                </label>
              </div>
            </div>

            {/* Video Preview */}
            {recordingType === 'webcam_only' && (
              <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem' }}>
                <video 
                  ref={videoRef}
                  autoPlay 
                  muted
                  playsInline
                  style={{ 
                    width: '100%', 
                    borderRadius: '0.5rem',
                    background: '#000',
                    transform: 'scaleX(-1)'
                  }}
                />
              </div>
            )}

            {/* Audio Only Indicator */}
            {recordingType === 'audio_only' && recordingState === 'recording' && (
              <div style={{ 
                background: 'white', 
                borderRadius: '0.75rem', 
                padding: '3rem', 
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                <div style={{ 
                  fontSize: '4rem', 
                  marginBottom: '1rem',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}>
                  🎤
                </div>
                <p style={{ fontSize: '1.25rem', fontWeight: '600', color: '#dc2626' }}>
                  Recording Audio...
                </p>
              </div>
            )}

            {/* Recording Controls */}
            <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1.5rem' }}>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                {recordingState === 'idle' && (
                  <button
                    onClick={() => setShowConfirmModal(true)}
              
                    style={{
                      padding: '1rem 2rem',
                      background: selectedKids.length > 0 ? '#dc2626' : '#e5e7eb',
                      color: selectedKids.length > 0 ? 'white' : '#9ca3af',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      cursor: selectedKids.length > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Camera size={24} /> Start Recording
                  </button>
                )}

                {recordingState === 'recording' && (
                  <button
                    onClick={stopRecording}
                    style={{
                      padding: '1rem 2rem',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Square size={24} /> Stop Recording
                  </button>
                )}

                {recordingState === 'stopped' && !isUploading && (
                  <button
                    onClick={resetRecording}
                    style={{
                      padding: '1rem 2rem',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <RotateCcw size={24} /> Record Again
                  </button>
                )}
              </div>

              {isUploading && (
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <p style={{ fontWeight: '600', color: '#4b5563' }}>Uploading recording...</p>
                </div>
              )}
            </div>
          </div>

          {/* Kid Selection */}
          <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem', fontWeight: '600' }}>
              Assign to Kids
              {selectedKids.length > 0 && (
                <span style={{ color: '#10b981', marginLeft: '0.5rem' }}>
                  ({selectedKids.length} selected)
                </span>
              )}
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
              Select at least one child to record for
            </p>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {availableKids.map(kid => (
                <label
                  key={kid.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    border: selectedKids.find(k => k.id === kid.id) ? '2px solid #10b981' : '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    marginBottom: '0.5rem',
                    cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed',
                    background: selectedKids.find(k => k.id === kid.id) ? '#f0fdf4' : 'white',
                    opacity: recordingState === 'idle' ? 1 : 0.5
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!selectedKids.find(k => k.id === kid.id)}
                    onChange={() => {
                      setSelectedKids(prev => {
                        const isSelected = prev.find(k => k.id === kid.id);
                        if (isSelected) {
                          return prev.filter(k => k.id !== kid.id);
                        } else {
                          return [...prev, kid];
                        }
                      });
                    }}
                    disabled={recordingState !== 'idle'}
                  />
                  <div>
                    <div style={{ fontWeight: '600' }}>{kid.first_name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{kid.familyName}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Start Recording Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: '#1f2937' }}>
              Ready to Record?
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
              Your recording will start immediately after confirming.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#e5e7eb',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={startRecording}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Start Recording
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save or Re-record Modal */}
      {showSaveConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: '#1f2937' }}>
              Recording Uploaded Successfully! 🎉
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              Your recording has been uploaded. Would you like to save it and continue, or delete it and record again?
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleDeleteAndRerecord}
                disabled={isUploading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <RotateCcw size={18} /> Delete & Re-record
              </button>
              <button
                onClick={handleSaveAndContinue}
                disabled={isUploading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.5 : 1
                }}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default PersonalBookRecordingPage;
